import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { AlertTriangle, Check, Flag } from "react-feather";
import { Carousel } from "@cinesys/ui/components";
import { SPOTTER_STRINGS } from "../../strings";
import { AnchorJobStart, AnchorTask, useClipThumbnail } from "../service";
import { reelCardLabel, reelCardVisual } from "../reelCard";
import { useInView } from "../useInView";

/**
 * The reel band (full-page anchor editor, Option A "stage over sheet") — the shared `Carousel`
 * (`@cinesys/ui/components`, same one `TokenChip.tsx`/`SpotterHeader.tsx`'s tab strip already use
 * elsewhere in this app) showing every anchor's own clip, the current one enlarged/ringed,
 * confirmed cards carrying a tick + the uid they resolved to, an out-of-order card ringed red with
 * a note. The game-start boundary (`job.start`, set in Phase A) leads the reel as its own
 * non-interactive frame — a real clip, not a subtitle above the strip — since it's immutable from
 * this screen (no "Change" affordance here, Phase A already committed it).
 *
 * Clicking any anchor card jumps back to it (`onSelect`) — the click IS the re-edit affordance;
 * there's no separate "Change" control anywhere else. `Carousel` supplies its own scroll
 * arrows/fade edges for BROWSING the strip; stepping onto a specific anchor to edit it is done by
 * clicking its card, which smooth-scrolls it into view (the `useEffect` below) rather than
 * snapping — "switching anchors" reads as a real transition, not a jump cut.
 */

const CarouselFrame = styled.div`
  background: var(--spotterSurface);
  border: 1px solid var(--spotterLine);
  border-radius: 10px;
  padding: 10px 8px;
  flex: none;
`;

const TONE_BORDER: Record<string, string> = {
  pending: "var(--spotterLine2)",
  resolved: "rgba(111, 191, 149, 0.5)",
  outOfOrder: "var(--spotterDanger)",
};

/** Cards genuinely resize on focus (`FOCUSED_CARD_WIDTH_PX`/`UNFOCUSED_CARD_WIDTH_PX`,
 *  `THUMB_HEIGHT_PX`/`UNFOCUSED_THUMB_HEIGHT_PX`) — a real `width`/`height` transition, not a
 *  paint-time `transform: scale()` standing in for it (that made every card's box, including its
 *  text, occupy the SAME footprint always and just look tinier when "unfocused", which read as
 *  clips not actually shrinking and their labels going blurry-small — Addison: "made the width
 *  that the clips take up even when they're small be fixed... shrinking the font sizes"). The
 *  outer carousel row's own height still doesn't wobble mid-transition, though: `CARD_HEIGHT_PX`
 *  (below) is passed as `Carousel`'s own `height` prop, a hard-pinned row height independent of
 *  any single card's current size — without it, two cards animating in OPPOSITE directions at
 *  once (the old focus shrinking from 84 while the new one grows from 54) can have neither one at
 *  84 for an instant mid-transition, and a row height that's simply "the tallest child right now"
 *  would visibly dip and recover. Pinning the row height sidesteps that without faking the cards
 *  themselves. */
const FOCUSED_CARD_WIDTH_PX = 150;
const UNFOCUSED_CARD_WIDTH_PX = 96;
const THUMB_HEIGHT_PX = 84;
const UNFOCUSED_THUMB_HEIGHT_PX = 54;
const LABEL_LINE_HEIGHT_PX = 12;
const CARD_GAP_PX = 4;
// Three stacked children (Thumb, ClipId, ClipPick) in a flex column with `gap: CARD_GAP_PX` —
// that's 2 gaps, not 3. Sized off the FOCUSED thumb height — the tallest any card ever gets.
const CARD_HEIGHT_PX =
  THUMB_HEIGHT_PX + CARD_GAP_PX * 2 + LABEL_LINE_HEIGHT_PX * 2;

const Clip = styled.button<{ $focused: boolean }>`
  all: unset;
  box-sizing: border-box;
  flex: none;
  width: ${(p) =>
    p.$focused ? FOCUSED_CARD_WIDTH_PX : UNFOCUSED_CARD_WIDTH_PX}px;
  display: flex;
  flex-direction: column;
  gap: ${CARD_GAP_PX}px;
  cursor: pointer;
  transition: width 0.2s ease;
`;

// Byte-identical to `Clip` today — the game-start frame is a real (if non-editable) reel card,
// not a visually distinct thing.
const StartFrame = styled(Clip)``;

const Thumb = styled.div<{ $tone: string; $focused: boolean }>`
  position: relative;
  box-sizing: border-box;
  height: ${(p) =>
    p.$focused ? THUMB_HEIGHT_PX : UNFOCUSED_THUMB_HEIGHT_PX}px;
  border-radius: 6px;
  border: ${(p) => (p.$focused ? "2px" : "1px")} solid
    ${(p) => (p.$focused ? "var(--spotterAccent)" : TONE_BORDER[p.$tone])};
  box-shadow: ${(p) =>
    p.$focused ? "0 0 0 3px var(--spotterAccentSoft)" : "none"};
  background: linear-gradient(
    150deg,
    var(--spotterRaised),
    var(--spotterSlate) 60%,
    var(--spotterRaised)
  );
  overflow: hidden;
  transition: height 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
`;

const StartThumb = styled(Thumb)`
  border-style: dashed;
  border-color: var(--spotterAccentLine);
`;

const ThumbImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

/** Fetches (once actually scrolled into view — `useInView`) and renders one clip's still-image
 *  thumbnail, absolutely filling whichever `Thumb`/`StartThumb` box it's mounted inside. Renders
 *  nothing (leaving the plain gradient background showing through) until a real image is ready —
 *  iconik may not have a keyframe yet, which is a real, expected state, not an error. */
const ReelThumbImage = ({
  jobId,
  assetId,
}: {
  jobId: string;
  assetId: string;
}) => {
  const [inView, setInView] = useState(false);
  const viewRef = useInView<HTMLDivElement>(() => setInView(true), "200px");
  const thumbnailQuery = useClipThumbnail(jobId, assetId, {
    enabled: inView,
  });
  return (
    <div ref={viewRef} style={{ position: "absolute", inset: 0 }}>
      {thumbnailQuery.data?.url && (
        <ThumbImg src={thumbnailQuery.data.url} loading="lazy" alt="" />
      )}
    </div>
  );
};

const Tick = styled.span<{ $warn?: boolean }>`
  position: absolute;
  top: 4px;
  left: 4px;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: ${(p) => (p.$warn ? "var(--spotterDanger)" : "var(--spotterOk)")};
  color: var(--spotterPaper);
  z-index: 1;
`;

const StartTick = styled.span`
  position: absolute;
  top: 4px;
  left: 4px;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: var(--spotterAccent);
  color: var(--spotterPaper);
  z-index: 1;
`;

// `line-height` is pinned rather than left to the font's default metrics — this is one of the two
// text rows `CARD_HEIGHT_PX` above adds up as a hard constant, not an estimate; a long, irregular
// clip name (this customer's are, e.g. "2025_R02_09-14-25_CLV_at_BLT_Ground_202_047_RP-lowres.mp4")
// never wraps to a second line (`white-space: nowrap` + ellipsis) or grows the row.
const ClipId = styled.span<{ $tone: string; $focused: boolean }>`
  font-size: 9.5px;
  line-height: ${LABEL_LINE_HEIGHT_PX}px;
  font-family: ui-monospace, Menlo, monospace;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${(p) =>
    p.$focused
      ? "var(--spotterAccent)"
      : p.$tone === "outOfOrder"
      ? "var(--spotterDanger)"
      : "var(--spotterInkFaint)"};
  font-weight: ${(p) => (p.$focused ? 600 : 400)};
`;

const StartLabel = styled.span`
  font-size: 9.5px;
  line-height: ${LABEL_LINE_HEIGHT_PX}px;
  font-family: ui-monospace, Menlo, monospace;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--spotterAccent);
  font-weight: 600;
`;

// Always rendered — `$visible` toggles `visibility`, never whether the row mounts at all — so
// every card reserves the same height regardless of whether it has a resolved pick yet.
const ClipPick = styled.span<{ $tone: string; $visible: boolean }>`
  font-size: 9.5px;
  line-height: ${LABEL_LINE_HEIGHT_PX}px;
  font-family: ui-monospace, Menlo, monospace;
  text-align: center;
  color: ${(p) =>
    p.$tone === "outOfOrder" ? "var(--spotterDanger)" : "var(--spotterOk)"};
  visibility: ${(p) => (p.$visible ? "visible" : "hidden")};
`;

export interface ReelStripProps {
  jobId: string;
  start: AnchorJobStart | null;
  /** Whether the game-start frame (rather than a real anchor) is what the stage below is
   *  currently showing — its own focus/ring treatment, independent of `currentAnchorId`. */
  startFocused: boolean;
  onSelectStart: () => void;
  anchors: AnchorTask[];
  currentAnchorId: number;
  resolvedUidByAnchorId: Map<number, string>;
  outOfOrderIds: Set<number>;
  onSelect: (anchorId: number) => void;
}

const ReelStrip = ({
  jobId,
  start,
  startFocused,
  onSelectStart,
  anchors,
  currentAnchorId,
  resolvedUidByAnchorId,
  outOfOrderIds,
  onSelect,
}: ReelStripProps) => {
  const t = SPOTTER_STRINGS.filmsGround.phaseB;
  const cardRefs = useRef<Map<number, HTMLButtonElement>>(new Map());
  const startFrameRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const target = startFocused
      ? startFrameRef.current
      : cardRefs.current.get(currentAnchorId);
    target?.scrollIntoView({
      block: "nearest",
      inline: "center",
      behavior: "smooth",
    });
  }, [currentAnchorId, startFocused]);

  return (
    <CarouselFrame role="region" aria-label={t.reelStripAria}>
      <Carousel
        height={CARD_HEIGHT_PX}
        gap={8}
        justify="start"
        alignItems="center"
        arrowSize={26}
        fadeColor="var(--spotterSurface)"
      >
        {start && (
          <StartFrame
            ref={startFrameRef}
            type="button"
            $focused={startFocused}
            aria-label={t.startRowText(start.summaryText)}
            aria-current={startFocused}
            onClick={onSelectStart}
          >
            <StartThumb $tone="pending" $focused={startFocused}>
              <ReelThumbImage jobId={jobId} assetId={start.assetId} />
              <StartTick aria-hidden="true">
                <Flag size={9} />
              </StartTick>
            </StartThumb>
            <StartLabel>{t.startFrameLabel}</StartLabel>
          </StartFrame>
        )}
        {anchors.map((anchor) => {
          const resolvedUid =
            resolvedUidByAnchorId.get(anchor.anchorId) ?? null;
          const outOfOrder = outOfOrderIds.has(anchor.anchorId);
          const visual = reelCardVisual(
            anchor.anchorId,
            currentAnchorId,
            !!resolvedUid,
            outOfOrder
          );
          const label = reelCardLabel(anchor.anchorId, anchor.clipName);
          const ariaLabel = outOfOrder
            ? t.reelOutOfOrderAria(label, resolvedUid ?? "")
            : resolvedUid
            ? t.reelConfirmedAria(label, resolvedUid)
            : t.reelPendingAria(label);
          return (
            <Clip
              key={anchor.anchorId}
              type="button"
              $focused={visual.focused}
              aria-label={ariaLabel}
              aria-current={visual.focused}
              ref={(el) => {
                if (el) {
                  cardRefs.current.set(anchor.anchorId, el);
                } else {
                  cardRefs.current.delete(anchor.anchorId);
                }
              }}
              onClick={() => onSelect(anchor.anchorId)}
            >
              <Thumb $tone={visual.tone} $focused={visual.focused}>
                <ReelThumbImage jobId={jobId} assetId={anchor.clipAssetId} />
                {visual.tone === "resolved" && (
                  <Tick aria-hidden="true">
                    <Check size={10} />
                  </Tick>
                )}
                {visual.tone === "outOfOrder" && (
                  <Tick $warn aria-hidden="true">
                    <AlertTriangle size={10} />
                  </Tick>
                )}
              </Thumb>
              <ClipId $tone={visual.tone} $focused={visual.focused}>
                {label}
              </ClipId>
              <ClipPick $tone={visual.tone} $visible={!!resolvedUid}>
                {resolvedUid
                  ? outOfOrder
                    ? `${resolvedUid} · ${t.outOfOrderBadge}`
                    : resolvedUid
                  : " "}
              </ClipPick>
            </Clip>
          );
        })}
      </Carousel>
    </CarouselFrame>
  );
};

export default ReelStrip;
